import type {ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {createPortal} from 'react-dom';
import {Text, TextSize} from '@Components/text';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ContextualMenuSliderOptions} from '@Components/contextual-menu/components/contextual-menu-slider-options';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {QR_BACKGROUND_CORNER_RADIUS_MAX, QR_MARGIN_MAX} from '@Libraries/qr-code/qr-code.types';
import {hasQROutline} from '@Libraries/qr-code/qr-render.library';
import {QRStylePreviewTile} from '@Components/qr-code-generator/components/qr-style-preview-tile/qr-style-preview-tile';
import {QRColorOption} from '@Components/qr-code-generator/components/qr-color-option/qr-color-option';
import type {QRCodeStyle, QRStyleOption} from '@Components/qr-code-generator/qr-code-generator.types';
import {
  getDefaultQRCodeStyle,
  QR_CORNER_STYLE_OPTIONS,
  QR_DOT_STYLE_OPTIONS,
  QR_OUTLINE_THICKNESS_MAX,
  QR_OUTLINE_THICKNESS_MIN,
  QR_OUTLINE_THICKNESS_OFF,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {getQRStylePreviewUrl, isQRStyleOverrideApplied} from '@Components/qr-code-generator/qr-code-generator.library';
import {useDismissOnEscape, useQRCodePreviews} from '@Components/qr-code-generator/qr-code-generator-hooks';
import {QRPreviewPanel} from '@Components/qr-code-generator/components/qr-preview-panel/qr-preview-panel';
import {DEFAULT_OUTLINE_THICKNESS} from '@Components/poster-editor/components/poster-editing-side-panel/components/vector-outline-option/vector-outline-option.types';
import styles from './qr-style-modal.module.scss';
import type {QRStyleModalProps} from './qr-style-modal.types';

export function QRStyleModal({style, previewPayload, logoDataUrl, onCommit, onClose}: QRStyleModalProps): ReactElement {
  const [draft, setDraft] = useState<QRCodeStyle>(style);
  useDismissOnEscape(onClose);

  const hasBackground = draft.backgroundColor[3] > 0;
  const hasOutline = hasQROutline(draft.outlineThickness);

  const {previewUrls, isRendering} = useQRCodePreviews([previewPayload], {...draft, logoDataUrl});

  const onStyleChange = useCallback((patch: Partial<QRCodeStyle>): void => {
    setDraft((current): QRCodeStyle => {
      return {...current, ...patch};
    });
  }, []);

  const onReset = useCallback((): void => {
    setDraft(getDefaultQRCodeStyle());
  }, []);

  const onDone = useCallback((): void => {
    onCommit(draft);
  }, [draft, onCommit]);

  const renderStyleTiles = (options: QRStyleOption[]): ReactElement => {
    return (
      <div className={styles.qrStyleTileGrid}>
        {options.map((option): ReactElement => {
          return (
            <QRStylePreviewTile
              key={option.previewFileName}
              previewUrl={getQRStylePreviewUrl(option.previewFileName)}
              label={window.i18next.t(option.labelKey)}
              isSelected={isQRStyleOverrideApplied(draft, option.override)}
              onSelect={(): void => {
                onStyleChange(option.override);
              }}
            />
          );
        })}
      </div>
    );
  };

  const renderSection = (label: string, content: ReactElement): ReactElement => {
    return (
      <section className={styles.qrStyleSection}>
        <Text val={label} size={TextSize.MD} bold />
        {content}
      </section>
    );
  };

  const onBackgroundToggle = (): void => {
    const [red, green, blue] = draft.backgroundColor;
    onStyleChange({backgroundColor: [red, green, blue, hasBackground ? 0 : 1]});
  };

  const onOutlineToggle = (): void => {
    onStyleChange({outlineThickness: hasOutline ? QR_OUTLINE_THICKNESS_OFF : DEFAULT_OUTLINE_THICKNESS});
  };

  const renderBackgroundColorOption = (): ReactElement | null => {
    if (!hasBackground) {
      return null;
    }
    return (
      <QRColorOption
        isForSubOption
        label={window.i18next.t('pmwjs_color')}
        selectedColor={draft.backgroundColor}
        onColorChange={(backgroundColor): void => {
          onStyleChange({backgroundColor});
        }}
      />
    );
  };

  const renderBackgroundOption = (): ReactElement => {
    return (
      <ContextualMenuToggleOption id="qr-generator-background-toggle" label={window.i18next.t('pmwjs_background')} isChecked={hasBackground} onClick={onBackgroundToggle}>
        {renderBackgroundColorOption()}
      </ContextualMenuToggleOption>
    );
  };

  const renderOutlineSubOptions = (): ReactElement | null => {
    if (!hasOutline) {
      return null;
    }
    return (
      <>
        <QRColorOption
          isForSubOption
          label={window.i18next.t('pmwjs_color')}
          selectedColor={draft.outlineColor}
          onColorChange={(outlineColor): void => {
            onStyleChange({outlineColor});
          }}
        />
        <ContextualMenuSliderOptions
          id="qr-generator-outline-thickness"
          label={window.i18next.t('pmwjs_thickness')}
          value={draft.outlineThickness}
          range={{min: QR_OUTLINE_THICKNESS_MIN, max: QR_OUTLINE_THICKNESS_MAX}}
          onChange={(outlineThickness: number): void => {
            onStyleChange({outlineThickness});
          }}
        />
      </>
    );
  };

  const renderOutlineOption = (): ReactElement => {
    return (
      <ContextualMenuToggleOption id="qr-generator-outline-toggle" label={window.i18next.t('pmwjs_outline')} isChecked={hasOutline} onClick={onOutlineToggle}>
        {renderOutlineSubOptions()}
      </ContextualMenuToggleOption>
    );
  };

  const renderOptionRows = (): ReactElement => {
    return (
      <ul className={styles.qrStyleOptionList}>
        <QRColorOption
          label={window.i18next.t('pmwjs_color')}
          selectedColor={draft.foregroundColor}
          onColorChange={(foregroundColor): void => {
            onStyleChange({foregroundColor});
          }}
        />
        {renderBackgroundOption()}
        <ContextualMenuSliderOptions
          id="qr-generator-roundness"
          label={window.i18next.t('pmwjs_roundness')}
          value={draft.backgroundCornerRadiusPercent}
          range={{min: 0, max: QR_BACKGROUND_CORNER_RADIUS_MAX}}
          onChange={(backgroundCornerRadiusPercent: number): void => {
            onStyleChange({backgroundCornerRadiusPercent});
          }}
        />
        <ContextualMenuSliderOptions
          id="qr-generator-padding"
          label={window.i18next.t('pmwjs_padding')}
          value={draft.marginPercent}
          range={{min: 0, max: QR_MARGIN_MAX}}
          onChange={(marginPercent: number): void => {
            onStyleChange({marginPercent});
          }}
        />
        {renderOutlineOption()}
      </ul>
    );
  };

  return createPortal(
    <div className={styles.qrStyleModalOverlay}>
      <div className={styles.qrStyleModal} role="dialog" aria-modal="true" aria-label={window.i18next.t('pmwjs_qr_generator_style_heading')}>
        <header className={styles.qrStyleModalHeader}>
          <Text val={window.i18next.t('pmwjs_qr_generator_style_heading')} size={TextSize.LG} bold className={styles.qrStyleModalTitle} />
          <Icon
            icon="icon-close"
            size={IconSize.SIZE_ICON_20}
            isUsedAsButton
            ariaLabel={window.i18next.t('pmwjs_qr_generator_close')}
            className={styles.qrStyleModalClose}
            onClick={onClose}
          />
        </header>

        <div className={styles.qrStyleModalContent}>
          <div className={styles.qrStyleModalBody}>
            {renderSection(window.i18next.t('pmwjs_qr_pattern'), renderStyleTiles(QR_DOT_STYLE_OPTIONS))}
            {renderSection(window.i18next.t('pmwjs_qr_corners'), renderStyleTiles(QR_CORNER_STYLE_OPTIONS))}
            {renderOptionRows()}
          </div>

          <aside className={styles.qrStyleModalPreview} aria-label={window.i18next.t('pmwjs_qr_generator_preview_alt')}>
            <QRPreviewPanel previewUrl={previewUrls[0] ?? null} isRendering={isRendering} payloadIssueText={null} />
          </aside>
        </div>

        <footer className={styles.qrStyleModalFooter}>
          <Button text={window.i18next.t('pmwjs_qr_generator_style_reset')} type={ButtonType.TERTIARY} size={ButtonSize.MEDIUM} onClick={onReset} />
          <Button text={window.i18next.t('pmwjs_qr_generator_style_done')} type={ButtonType.PRIMARY} size={ButtonSize.MEDIUM} onClick={onDone} />
        </footer>
      </div>
    </div>,
    document.body
  );
}
